/**
 * Shared style tokens for exception tables and their inset detail panels.
 *
 * Goal: give every exception report (and the expandable detail panels they
 * render) the same row hover, divider colour, toolbar divider, "something
 * is wrong here" tints, and detail-panel surface - sourced from
 * `poolwerxColors`. Anywhere a report was inlining a hex colour for these
 * purposes, swap it for an import from this file so future tweaks land in
 * one place.
 *
 * Two flavours are exported:
 *  - Raw string constants for use inside CSS variables / inline `sx`:
 *      `sx={{ backgroundColor: MISMATCH_ROW_BG }}`
 *  - `resultsCardSx` SX object for the Paper wrapper (the one cross-call
 *    pattern that's reused as a whole sx).
 *
 * Don't add Sx-object wrappers around the raw strings unless a real
 * second consumer appears - dead duplicates are pure maintenance load.
 */
import type { SxProps, Theme } from '@mui/material'
import { poolwerxColors } from './colors'

/**
 * Surface colour for the inset detail panel that sits inside an expanded
 * row's `Collapse`. A pale teal tint that reads as "still inside the
 * results card" without competing with the table itself.
 */
export const DETAIL_PANEL_BG = '#F5FAFB'

/**
 * Background tint for a row whose paired-system comparison does not agree
 * (Sales Compare). Light red so the eye lands on the bad row first.
 */
export const MISMATCH_ROW_BG = 'rgba(244, 67, 54, 0.08)'

/**
 * Background tint for an individual cell whose value is invalid / missing
 * (outlet address fields, etc.). Slightly different from MISMATCH_ROW_BG
 * because it sits next to good cells in the same row.
 */
export const INVALID_CELL_BG = '#FEF0EF'

/**
 * Border colour for the inter-row divider inside the table body and for
 * any Paper border that should align with row borders.
 */
export const TABLE_ROW_DIVIDER = '#E7ECEE'

/**
 * Subtle divider used between toolbar segments (count | per-page |
 * pagination), as the bottom border of the title bar, AND as the outer
 * Paper border. Matches MUI's theme `divider` so the exception card sits
 * at the same prominence as Product Catalogue Review's TabSection.
 */
export const TOOLBAR_DIVIDER = 'rgba(0, 0, 0, 0.12)'

/**
 * Hover tint applied via the `--row-bg` custom property on table rows
 * and the fullscreen-button hover state. Pale teal.
 */
export const ROW_HOVER_BG = '#E6F4F5'

/**
 * SaaS-polish usage tokens. These compose `poolwerxColors.aqua` /
 * `tealAccent` at the right alphas + a couple of neutral surface
 * colours. Anywhere an exception screen needs the "modern" look (page
 * header icon tile, active issue-tab, softer table rows), import these
 * - don't inline new hexes / rgba strings in components.
 */
export const ICON_TILE_BG = `${poolwerxColors.aqua}1F` // aqua ~12% alpha
export const TAB_ACTIVE_BG = `${poolwerxColors.aqua}1A` // aqua ~10% alpha
export const TAB_ACTIVE_BORDER = poolwerxColors.tealAccent
export const ZEBRA_BG = '#FAFBFC'
export const ROW_HOVER_SOFT = '#F3F8F9'

/**
 * Closed-view row treatment. When the exception page is showing the
 * Closed slice (?status=closed), every row renders with a muted slate-
 * grey background plus reduced opacity so reviewers can tell at a
 * glance that they're looking at resolved/ignored exceptions, not the
 * active open list.
 */
export const CLOSED_ROW_BG = '#F0F2F4'
export const CLOSED_ROW_OPACITY = 0.7

/**
 * Active-row treatment for the note-panel spotlight. When the user
 * opens the side panel on a row, the table dims every other row (so
 * the eye snaps to the source row) and the source row gets a soft
 * teal background + a 4 px teal left-border accent.
 *
 * Numbers (deliberately tuned away from the earlier MUI teal[100]
 * which read as "this row is in a special status state" rather than
 * "this row is selected"):
 *   - `ACTIVE_ROW_BG` (#E6F7FA) - very light cyan-teal; reads as a
 *     selection tint, not a status colour.
 *   - `ACTIVE_ROW_HOVER_BG` (#D8F0F4) - a shade darker so hovering
 *     the active row still gives feedback without losing the
 *     selection contrast.
 *   - `ACTIVE_ROW_ACCENT` (#007F98) - poolwerx teal; saturated
 *     left-edge accent that ties the row to the side panel.
 *   - `INACTIVE_ROW_OPACITY` (0.4) - dims sibling rows but keeps
 *     their contents legible (a complete blackout would be over
 *     the top).
 */
export const ACTIVE_ROW_BG = '#E6F7FA'
export const ACTIVE_ROW_HOVER_BG = '#D8F0F4'
export const ACTIVE_ROW_ACCENT = '#007F98'
export const INACTIVE_ROW_OPACITY = 0.4

/**
 * Inner table-card chrome. The outer Results card stays all-white;
 * the table inside sits as its own rounded sub-card with a slight
 * left/right margin and a 1 px neutral border - the "card-in-card"
 * shape that matches Product Catalogue Review.
 *
 * `clip-path` is used (not `overflow: hidden`) so the rounded corners
 * mask the visible content without creating a new scroll ancestor -
 * keeps the sticky-to-window table head chain intact.
 */
export const INNER_TABLE_CARD_BORDER = '1px solid rgba(0, 0, 0, 0.08)'
export const INNER_TABLE_CARD_RADIUS = '10px'

/**
 * Slightly darker top edge for the inset table card so the transition
 * from the toolbar (white) into the group/column header row reads as a
 * defined seam. Keeps the other three sides on the lighter
 * `INNER_TABLE_CARD_BORDER` so the card doesn't feel boxed in.
 */
export const INNER_TABLE_CARD_TOP_BORDER = '1px solid rgba(0, 0, 0, 0.16)'

/**
 * Inspector-variant Results card wrapper. Outlined Paper with rounded
 * corners and an all-white background - the toolbar sits flush at the
 * top, the table inside is inset as its own sub-card.
 *
 * `overflow: 'visible'` is deliberate - the sticky toolbar + table header
 * rows pin to the viewport and need to escape the Paper's bounding box on
 * scroll. Clipping here breaks the sticky chain.
 */
export const resultsCardSx: SxProps<Theme> = {
  overflow: 'visible',
  borderRadius: '12px',
  borderColor: 'rgba(0, 0, 0, 0.08)',
  backgroundColor: '#FFFFFF',
  // 16 px internal padding matches IssuePanel so the two cards read
  // as a matching pair (Filter issues + Results) on the page.
  p: 2,
}

/**
 * Group-header row ("FRANCHISE | IDENTITY | …" in the matrix variant).
 * Neutral grey band with stronger uppercase labels so the row reads
 * as defined hierarchy without competing with the table body.
 */
export const GROUP_HEADER_BG_INSPECTOR = '#F1F5F6'

/**
 * Group-header label colour. Deep teal so the uppercase labels still
 * read as Poolwerx-toned against the neutral grey band.
 */
export const GROUP_HEADER_TEXT = '#006F78'

/**
 * Bottom border between the group-header row and the column-header row.
 * Slightly darker than the body row divider so the two header bands
 * stack with a clear seam.
 */
export const GROUP_HEADER_BOTTOM_BORDER = '1px solid #D8E2E5'

/**
 * Column-label row background - one shade lighter than the group-header
 * row so the two bands read as a coherent stack with a clear hierarchy.
 * Applied to ALL inspector tables (matrix + simple) so the column
 * headers stay consistent across reports.
 */
export const COLUMN_HEADER_BG_INSPECTOR = '#F7F8F9'

/**
 * Column-label text colour. Dark slate for crisp contrast against
 * `COLUMN_HEADER_BG_INSPECTOR`.
 */
export const COLUMN_HEADER_TEXT = '#20292C'

/**
 * Bottom border on the column-header row - 1 px neutral grey, anchors
 * the data beneath without feeling heavy.
 */
export const HEADER_BOTTOM_BORDER = '1px solid #C9D4D8'

/**
 * Sorted-column state. The active sort label switches text colour to
 * near-black and font-weight to 800; the sort arrow stays neutral grey
 * so the column doesn't end up double-emphasised.
 */
export const SORTED_HEADER_TEXT = '#111827'
export const SORT_ICON_COLOR = '#5F6B70'

/**
 * Drop shadow applied to the bottom of the column-header row. With
 * sticky-to-page bound headers, the shadow sits over the row beneath
 * whenever the user scrolls into the data; it stays subtle enough not
 * to look heavy when the table is at the top of the viewport.
 */
export const STICKY_HEADER_SHADOW = '0 4px 6px -3px rgba(0, 0, 0, 0.10)'

/**
 * Soft drop shadow on the drawer's inset cards (the healthy-empty / nothing-to-
 * fix state cards). One token so the cards stay visually flush and no raw
 * rgba() literal lives in a component sx.
 */
export const CARD_SHADOW = '0 1px 2px rgba(15,23,42,0.04)'

/**
 * Right border placed on the last cell of a column group (for groups
 * marked `separatorAfter: true`). Applied identically on the header row
 * and matching body cells so the divider reads as one continuous line
 * through the whole table. 1 px / light grey keeps the table SaaS-modern
 * rather than spreadsheet-heavy.
 */
export const GROUP_SEPARATOR_BORDER = '1px solid #D8E0E3'

/**
 * Right-side drop shadow on the last frozen (sticky-left) column. Tells
 * the user that columns to the left of the shadow are pinned while the
 * rest of the table scrolls horizontally beneath the shadow.
 */
export const FREEZE_SHADOW = '4px 0 6px -3px rgba(0, 0, 0, 0.18)'

/**
 * Accent colour for the left border of an `ExceptionDetailPanel`. Teal for
 * FP-actionable issues (matches owner-pill colour), grey for PSC-handled.
 */
export const detailPanelAccent = {
  fp: poolwerxColors.teal,
  psc: poolwerxColors.grey[400],
} as const
